import React from 'react';
import {
  Anchor,
  AppShell,
  Avatar,
  Badge,
  Box,
  Group,
  Image,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconBell, IconChevronDown, IconLogout } from '@tabler/icons-react';
import { Link, Outlet, useMatch } from 'react-router-dom';
import {
  useClientNotificationUnreadCount,
  useClientNotificationUnreadCounts,
  useMyMines,
} from '../../application/queries/useMineClient';
import { useMineMemberships } from '../../application/store/authStore';
import { RouteErrorBoundary, ErrorFallback } from '../components/ErrorBoundary';
import { SessionTimeoutWarning } from '../components/auth/SessionTimeoutWarning/SessionTimeoutWarning';
import { distinctMineIds } from '../../domain/value-objects/MineMembership';
import { getUserRoleLabel } from '../../domain/value-objects/UserRole';
import { useAuth } from '../hooks/useAuth';
import { useSessionMonitor } from '../hooks/useSessionMonitor';
import { buildRoute, CLIENT_ROUTES } from '../routing/routes';
import LogoSvg from '../../assets/images/logo.svg';
import './clientArea.css';

/**
 * MineClientLayout — chrome for the client area (sprint-217, epic-036 Phase 1 / 1D, DR-008).
 *
 * ── Why this looks nothing like MineMinerLayout ──────────────────────────────────────────
 *
 * A client user and an internal rep can be signed in to the same origin, and the single most
 * consequential question either of them can ask is "am I looking at my own mine's data, or at
 * MineMiner's book of prospects?" If the two areas share chrome, that question has no visual
 * answer. The divergence below is a safety property, not a style preference.
 *
 *   · No sidebar. The staff tree has ~25 destinations and earns its 250px navbar; a client
 *     has their mines and what hangs off them. A persistent nav that is four-fifths empty
 *     reads as an unfinished product, so the mine scope goes in the top bar instead, where
 *     it is always visible.
 *   · ~~Dark top bar against the staff area's light one~~ — 🔧 superseded by sprint-273
 *     S006/D4 (PO, 2026-09-24): the client area now wears the staff app's light bar, grey page
 *     and white cards, and the phone app follows. The "different place" signal is carried by
 *     the NAME (MyMineMiner vs MineMiner, below) and the scope chip, not by the bar's colour.
 *     The client area's darker text and badge colours live in clientArea.css.
 *   · Monospace for every federal identifier. Mine ids and citation numbers get transcribed
 *     off paper and compared character by character; a proportional face makes 1/l and 0/O
 *     ambiguous exactly where being wrong is expensive.
 *   · No animation anywhere. This is a compliance surface people arrive at with a deadline;
 *     motion would add nothing and cost attention.
 *   · The brand here is MyMineMiner, not MineMiner (sprint-253 goal 1; sprint-252/S003/D3).
 *     The epic charters this product "under the independent MyMineMiner brand" (DR-001), and
 *     the split does double duty: staff chrome says MineMiner, client chrome says MyMineMiner,
 *     so the "which book am I looking at?" question above now has a *named* answer as well as
 *     a coloured one. The mark + wordmark are the one brand moment in this bar — the scope
 *     chip stays its signature element, and colour stays with the citation.
 *
 * Authentication + role gating happen in `MineClientRoute`, which wraps this layout in
 * AppRoutes — the same split the staff area uses.
 */
export const MineClientLayout: React.FC = (): React.ReactElement => {
  const { user, logout } = useAuth();
  const memberships = useMineMemberships();

  // Slide the session while the area is in use, same as the staff layout.
  useSessionMonitor();

  // sprint-273 S006/D4: the client area's colour overrides (clientArea.css) key on this class on
  // <html>, so they reach portals (menus, modals) too, and they leave with the layout.
  React.useEffect(() => {
    document.documentElement.classList.add('mm-client-area');
    return () => document.documentElement.classList.remove('mm-client-area');
  }, []);

  const mineIds = distinctMineIds(memberships);

  // goal 3 — the name for the chip when exactly one mine is in scope. The query is shared
  // with ClientMinesPage through react-query's cache, so this costs no extra round trip on
  // the common path, and the chip degrades to the id alone while it is in flight.
  const { data: mines } = useMyMines();
  const scopedMineName =
    mineIds.length === 1
      ? mines?.find((mine) => mine.mineId === mineIds[0])?.mineName ?? null
      : null;

  // ── The unread badge (sprint-227, 3F goal 4) ──────────────────────────────────────────
  //
  // 🔴 THIS LAYOUT IS NOT MINE-SCOPED AND THE UNREAD COUNT IS. The chrome wraps every client route,
  // including the mines list, while the endpoint answers per mine. So the mine is taken from the
  // ROUTE when the user is inside one, and falls back to their only mine when they have exactly
  // one — the case the scope chip below already special-cases.
  //
  // ⚠️ With several mines and no mine in the URL there is deliberately NO SUMMED NUMBER — a
  // single figure spanning tenants is the one thing this chrome exists to never show.
  //
  // 🔧 sprint-252 (4H): DR-041 ruling 4 supersedes this rule FOR THE LEGAL PORTFOLIO PAGE ONLY —
  // both grounds above dissolve there (its numbers click through to true per-mine surfaces, and
  // the reader holds an explicit grant at every mine in the sum). This badge, and every other
  // piece of chrome, keeps the rule; the supersession lives in ClientPortfolioPage, not here.
  //
  // 🔧 sprint-254 goal 3 (A3): what WAS "no badge at all" outside a mine is now a bell MENU of
  // per-mine rows — each row is one mine's own count and clicks through to that mine's feed, so
  // both of the original grounds are honoured (no cross-tenant figure ever renders; every number
  // leads somewhere true). The no-sum rule itself is untouched.
  const mineMatch = useMatch(`${CLIENT_ROUTES.CLIENT_MINE}/*`);
  const routeMineId = mineMatch?.params.mineId ?? null;
  const badgeMineId = routeMineId ?? (mineIds.length === 1 ? mineIds[0] ?? null : null);

  // The multi-mine bell's per-mine counts. Empty when a single mine is in scope — the hook
  // list must be unconditional, and an empty list issues no reads.
  const multiMineIds = badgeMineId === null ? mineIds : [];
  const multiMineUnread = useClientNotificationUnreadCounts(multiMineIds);
  const anyMineHasUnread = multiMineUnread.some((query) => (query.data ?? 0) > 0);

  // ── The role label (sprint-253 goal 3) ────────────────────────────────────────────────
  //
  // "Which hat am I wearing here" sits beside "whose data is this": a Supervisor, an Admin
  // and a Legal reader get materially different surfaces (tabs, write affordances, the
  // portfolio), and the chrome should say why rather than leave the difference to be
  // discovered. Same resolution as the badge — the route's mine, or the only mine — and,
  // like the badge, deliberately NOTHING with several mines and no mine in the URL: a role
  // can differ per mine, so a label spanning mines could lie about the one you open next.
  //
  // From the CLAIMS, like the rest of this chip cluster: chrome that draws on first paint,
  // a rendering aid and never an access decision (DR-005 clause 2). A user can hold more
  // than one role at the same mine; every held hat renders.
  const roleLabels =
    badgeMineId === null
      ? []
      : memberships
          .filter((membership) => membership.mineId === badgeMineId)
          .map((membership) => getUserRoleLabel(membership.role));

  // ⚠️ Reading the count marks nothing read. Only the feed's own control writes readAtUtc.
  const { data: unreadCount } = useClientNotificationUnreadCount(badgeMineId ?? '', {
    enabled: badgeMineId !== null,
  });

  const handleLogout = (): void => {
    void logout();
  };

  const initials = (user?.displayName ?? user?.email ?? '?')
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <AppShell header={{ height: 56 }} padding="md">
      {/* S006/D4: the staff bar's look (white, hairline edge, violet wordmark). */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" miw={0}>
            {/* Home is the mines list — the one destination every client role can stand on.
                Comfortaa is the brand face and appears in this area only here, exactly as the
                staff bar spends it only on its own wordmark. */}
            {/* flexShrink 0: the bar is nowrap, and without it the brand is the side that gives
                way at 390px — the chip draws over the wordmark's tail (S005 walk, W1). The chip
                is the element built to give way: it truncates to an ellipsis. */}
            <UnstyledButton
              component={Link}
              to={CLIENT_ROUTES.CLIENT_MINES}
              aria-label="MyMineMiner home"
              styles={{ root: { flexShrink: 0 } }}
            >
              <Group gap={8} wrap="nowrap">
                <Image src={LogoSvg} alt="" h={28} w="auto" />
                <Text fw={700} size="lg" c="brandPrimary.6" ff="Comfortaa, sans-serif">
                  MyMineMiner
                </Text>
              </Group>
            </UnstyledButton>

            {/* The scope chip — this area's signature element.
                Tenancy is the product's central invariant, so the chrome says which mines
                you are inside of at all times rather than burying it in a page-level
                dropdown.

                🔧 goal 3: the NAME now appears when one mine is in scope, from the server
                query — the token's claims carry ids only. The id stays with it rather than
                being replaced, and stays monospace, because the id is what someone matches
                against the citation in their hand. When the name has not loaded yet, or the
                mine has no MSHA row, the id alone is still a complete answer.

                🔴 Rendered from the CLAIMS for the count, deliberately: this is chrome that
                must draw on first paint, and it is a rendering aid, never an access decision
                (DR-005 clause 2). The name is an enrichment on top, not a gate. */}
            {mineIds.length > 0 && (
              <Badge
                variant="light"
                color="brandPrimary"
                size="lg"
                radius="sm"
                styles={{
                  root: { textTransform: 'none' },
                  // Only the id is monospace. A mine's NAME is not a federal identifier, and
                  // setting it in the identifier face would spend that signal for nothing.
                  label: { fontFamily: 'var(--mantine-font-family-sans)' },
                }}
              >
                {mineIds.length === 1 ? (
                  <Group gap={6} wrap="nowrap" component="span">
                    {scopedMineName && <span>{scopedMineName}</span>}
                    <Text
                      component="span"
                      inherit
                      ff="monospace"
                      c={scopedMineName ? 'dimmed' : undefined}
                    >
                      {mineIds[0]}
                    </Text>
                  </Group>
                ) : (
                  `${mineIds.length} mines`
                )}
              </Badge>
            )}

            {/* Quiet by design: the scope chip stays the signature element, and the role is
                context, not a warning. Hidden on the narrowest screens — at 390px the chip
                and brand already spend the bar. */}
            {roleLabels.length > 0 && (
              <Text size="xs" c="dimmed" visibleFrom="sm" styles={{ root: { whiteSpace: 'nowrap' } }}>
                {roleLabels.join(' · ')}
              </Text>
            )}
          </Group>

          <Group gap="md" wrap="nowrap">
            {/* The badge is a LINK and reads as one — a count nobody can click is a count that
                only makes people anxious. It is text rather than a coloured dot for the reason the
                tables in this area spend colour once: an operator arrives here with a deadline,
                and the red on their screen should be the citation, not their inbox.

                🔧 sprint-254 goal 3 (A3): the bell renders at ZERO unread too, as a quiet grey
                icon — the feed is a place, not just an alert, and a Supervisor re-reading an
                Admin's rejection reason after marking it read needs a door that is still there.
                The count text stays conditional; unread is what brightens it to white. */}
            {badgeMineId !== null && (
              <Anchor
                component={Link}
                to={buildRoute(CLIENT_ROUTES.CLIENT_MINE_NOTIFICATIONS, {
                  mineId: badgeMineId,
                })}
                size="sm"
                c={(unreadCount ?? 0) > 0 ? 'brandPrimary.6' : 'gray.7'}
                underline="never"
                aria-label={
                  (unreadCount ?? 0) > 0
                    ? `${unreadCount} unread ${
                        unreadCount === 1 ? 'notification' : 'notifications'
                      }`
                    : 'Notifications'
                }
              >
                <Group gap={6} wrap="nowrap">
                  <IconBell size={16} />
                  {(unreadCount ?? 0) > 0 && (
                    <Text component="span" size="sm" fw={600} inherit>
                      {unreadCount}
                    </Text>
                  )}
                </Group>
              </Anchor>
            )}

            {/* 🔧 sprint-254 goal 3 (A3): several mines, no mine in the URL — a bell MENU, one
                row per mine, each row that mine's own count linking to that mine's feed. No sum
                anywhere (the comment at badgeMineId's derivation carries the rule); the trigger
                brightens from grey to white when any mine has unread — a signal without a
                cross-tenant figure. */}
            {badgeMineId === null && mineIds.length > 1 && (
              <Menu shadow="md" width={280} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton aria-label="Notifications by mine">
                    <IconBell
                      size={16}
                      color={
                        anyMineHasUnread
                          ? 'var(--mantine-color-brandPrimary-6)'
                          : 'var(--mantine-color-gray-7)'
                      }
                    />
                  </UnstyledButton>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label>Notifications</Menu.Label>
                  {mineIds.map((mineId, index) => {
                    const mineUnread = multiMineUnread[index]?.data;
                    const mineName =
                      mines?.find((mine) => mine.mineId === mineId)?.mineName ?? null;

                    return (
                      <Menu.Item
                        key={mineId}
                        component={Link}
                        to={buildRoute(CLIENT_ROUTES.CLIENT_MINE_NOTIFICATIONS, { mineId })}
                      >
                        <Group justify="space-between" wrap="nowrap" gap="sm">
                          {/* Same rule as the scope chip: the NAME in the sans face, the federal
                              id in monospace — the id is what someone matches against paper. */}
                          <Text component="span" size="sm" truncate>
                            {mineName ?? (
                              <Text component="span" inherit ff="monospace">
                                {mineId}
                              </Text>
                            )}
                          </Text>
                          {mineUnread !== undefined && (
                            <Text
                              component="span"
                              size="sm"
                              fw={mineUnread > 0 ? 600 : undefined}
                              c={mineUnread > 0 ? undefined : 'dimmed'}
                              styles={{ root: { whiteSpace: 'nowrap' } }}
                            >
                              {mineUnread} unread
                            </Text>
                          )}
                        </Group>
                      </Menu.Item>
                    );
                  })}
                </Menu.Dropdown>
              </Menu>
            )}

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton aria-label="Account menu">
                  <Group gap="sm" wrap="nowrap">
                    <Avatar color="brandPrimary" radius="xl" size="sm">
                      {initials}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm">{user?.displayName ?? user?.email}</Text>
                    </Box>
                    <IconChevronDown size={14} color="var(--mantine-color-gray-7)" />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={handleLogout}
                >
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* S006/D4: a light grey ground; cards and tables are the white surfaces on it. */}
      <AppShell.Main bg="gray.0">
        <Stack mih="calc(100vh - 56px)">
          <RouteErrorBoundary fallback={ErrorFallback}>
            <Outlet />
          </RouteErrorBoundary>
        </Stack>
      </AppShell.Main>

      <SessionTimeoutWarning />
    </AppShell>
  );
};
